import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface Badge {
  _id: string;
  type: 'verified_seller' | 'trusted_breeder' | 'ngo_rescue' | 'business_account';
  name: string;
  description: string;
  icon?: string;
  color?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserBadge {
  _id: string;
  user: {
    _id: string;
    name: string;
    username: string;
    email?: string;
    profileImage?: string;
  };
  badge: Badge;
  assignedBy: {
    _id: string;
    name: string;
    username: string;
  };
  assignedAt: string;
  expiresAt?: string | null;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

class BadgeService {
  async getBadges() {
    const response = await apiClient.get<ApiResponse<Badge[]>>('/badges');
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch badges');
    }
    return response.data.data;
  }

  async getBadgeById(id: string) {
    const response = await apiClient.get<ApiResponse<Badge>>(`/badges/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch badge');
    }
    return response.data.data;
  }

  async getUserBadges(userId: string) {
    const response = await apiClient.get<ApiResponse<UserBadge[]>>(`/badges/user/${userId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch user badges');
    }
    return response.data.data;
  }

  async assignBadge(userId: string, badgeId: string, expiresAt?: string | null, notes?: string) {
    const response = await apiClient.post<ApiResponse<UserBadge>>('/badges/assign', {
      userId,
      badgeId,
      expiresAt,
      notes
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to assign badge');
    }
    return response.data.data;
  }

  async removeBadge(userBadgeId: string) {
    const response = await apiClient.delete<ApiResponse<void>>(`/badges/user-badge/${userBadgeId}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to remove badge');
    }
  }

  async updateBadgeAssignment(userBadgeId: string, data: { expiresAt?: string | null; notes?: string; isActive?: boolean }) {
    const response = await apiClient.put<ApiResponse<UserBadge>>(`/badges/user-badge/${userBadgeId}`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update badge assignment');
    }
    return response.data.data;
  }

  async getUsersWithBadge(badgeId: string, page: number = 1, limit: number = 20) {
    const response = await apiClient.get<ApiResponse<{ userBadges: UserBadge[]; pagination: any }>>(
      `/badges/badge/${badgeId}/users?page=${page}&limit=${limit}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch users with badge');
    }
    return response.data.data;
  }
}

export default new BadgeService();

